import { css } from "@emotion/react";

/**
 * CSS Variable names for tenant-configurable colors.
 * These are set at runtime by the host web component, fed from either:
 *   1. HTML attribute overrides (e.g. <pollen-drawer primary-color="#aabbcc">)
 *   2. The tenant's `configJson.widget` payload via GET /v2/widget-config
 *   3. Bundle-owned CSS fallbacks when neither is present.
 *
 * Only these 8 colors are tenant-configurable. The wider palette
 * (info, danger, context/AI states, etc.) remains hardcoded.
 */
export const CSS_VAR_NAMES = {
  textColor: "--pollen-text-color",
  backgroundColor: "--pollen-background-color",
  primaryColor: "--pollen-primary-color",
  cardBackgroundColor: "--pollen-card-background-color",
  textSecondaryColor: "--pollen-text-secondary-color",
  borderColor: "--pollen-border-color",
  fontFamily: "--pollen-font-family",
  headingFontFamily: "--pollen-heading-font-family",
  // Input chrome (search-box, landing-search-box, context-trigger)
  accentColor: "--pollen-accent-color",
  iconAccentColor: "--pollen-icon-accent-color",
  inputBorderColor: "--pollen-input-border-color",
  inputBackgroundColor: "--pollen-input-background-color",
} as const;

/**
 * Per-token fallbacks used when the widget is loaded without a tenant config
 * response or with unset tenant colors. The API only returns explicitly
 * configured colors; fallback stays in CSS.
 */
const DEFAULTS = {
  textColor: "#223C72",
  backgroundColor: "#FFFFFF",
  primaryColor: "#2563eb",
  cardBackgroundColor: "#FDF9F3",
  textSecondaryColor: "#475D8A",
  borderColor: "#e5e7eb",
  accentColor: "#00ABB8",
  iconAccentColor: "#2563eb",
} as const;

const VAR_ACCENT = `var(${CSS_VAR_NAMES.accentColor}, ${DEFAULTS.accentColor})`;
const VAR_ICON_ACCENT = `var(${CSS_VAR_NAMES.iconAccentColor}, ${DEFAULTS.iconAccentColor})`;
const VAR_PRIMARY = `var(${CSS_VAR_NAMES.primaryColor}, ${DEFAULTS.primaryColor})`;
const VAR_TEXT = `var(${CSS_VAR_NAMES.textColor}, ${DEFAULTS.textColor})`;
const VAR_CARD_BG = `var(${CSS_VAR_NAMES.cardBackgroundColor}, ${DEFAULTS.cardBackgroundColor})`;
const VAR_INPUT_BG = `var(${CSS_VAR_NAMES.inputBackgroundColor}, color-mix(in srgb, ${VAR_ACCENT} 4%, white))`;
const VAR_INPUT_BORDER = `var(${CSS_VAR_NAMES.inputBorderColor}, color-mix(in srgb, ${VAR_ACCENT} 60%, transparent))`;

export const colors = {
  // ===== TENANT-CONFIGURABLE COLORS (driven by CSS vars) =====
  primary: `var(${CSS_VAR_NAMES.primaryColor}, ${DEFAULTS.primaryColor})`,
  text: `var(${CSS_VAR_NAMES.textColor}, ${DEFAULTS.textColor})`,
  textSecondary: `var(${CSS_VAR_NAMES.textSecondaryColor}, ${DEFAULTS.textSecondaryColor})`,
  textPlaceholder: `var(${CSS_VAR_NAMES.textSecondaryColor}, ${DEFAULTS.textSecondaryColor})`,
  mutedText: `var(${CSS_VAR_NAMES.textSecondaryColor}, ${DEFAULTS.textSecondaryColor})`,
  // Elevated card surface: suggestion cards, sources, inputs.
  background: `var(${CSS_VAR_NAMES.cardBackgroundColor}, ${DEFAULTS.cardBackgroundColor})`,
  white: `var(${CSS_VAR_NAMES.cardBackgroundColor}, ${DEFAULTS.cardBackgroundColor})`,
  // Drawer panel, chat container, and row-style elements.
  surface: `var(${CSS_VAR_NAMES.backgroundColor}, ${DEFAULTS.backgroundColor})`,
  border: `var(${CSS_VAR_NAMES.borderColor}, ${DEFAULTS.borderColor})`,
  cardHover: `color-mix(in srgb, ${VAR_CARD_BG} 92%, ${VAR_TEXT})`,
  cardActive: `color-mix(in srgb, ${VAR_CARD_BG} 86%, ${VAR_TEXT})`,

  // ===== ACCENT PALETTE (driven by --pollen-accent-color) =====
  // The accent color powers the sparkle icon, glow shadows, context-trigger
  // button gradient, and the soft borders on glass surfaces.
  // accentSoft / accentHover are derived from the accent via color-mix().
  accent: VAR_ACCENT,
  accentSoft: `color-mix(in srgb, ${VAR_ACCENT} 60%, white)`,
  accentHover: `color-mix(in srgb, ${VAR_ACCENT} 90%, black)`,
  // Card/row navigation arrow icon color. Independent from primary so tenants
  // can pick e.g. a bright blue arrow against a navy primary CTA.
  iconAccent: VAR_ICON_ACCENT,
  info: "#F0FEFF",
  textTertiary: "#6A707D",
  activeAlternative: "#BCF0F3",

  // Button variants (use primary CSS var for tenant control)
  primaryButton: `var(${CSS_VAR_NAMES.primaryColor}, ${DEFAULTS.primaryColor})`,
  primaryButtonHover: `var(${CSS_VAR_NAMES.textSecondaryColor}, ${DEFAULTS.textSecondaryColor})`,

  // Context & AI specific (static)
  context: "#eff6ff",
  contextBorder: "#bfdbfe",
  contextText: "#1d4ed8",

  aiBox: "#e0f2fe",
  aiBorder: "#bae6fd",
  aiText: "#0ea5e9",

  // UI states (static)
  subtleBorder: "#d1d5db",
  danger: "#dc2626",
  muted: "#9ca3af",

};

/**
 * Cross-platform system font stacks (mirrors `tenant-ai-widget`).
 * Optional tenant brand fonts override via `/v2/widget-config` →
 * `--pollen-font-family` / `--pollen-heading-font-family`.
 */
export const BODY_FONT_STACK = `
  -apple-system,
  BlinkMacSystemFont,
  "Segoe UI",
  Roboto,
  "Helvetica Neue",
  Arial,
  "Noto Sans",
  "Liberation Sans",
  sans-serif,
  "Apple Color Emoji",
  "Segoe UI Emoji",
  "Segoe UI Symbol",
  "Noto Color Emoji"
`
  .replaceAll(/\s+/g, " ")
  .trim();

export const HEADING_FONT_STACK = BODY_FONT_STACK;

export const fonts = {
  headingFont: css`
    font-family: var(${CSS_VAR_NAMES.headingFontFamily}, ${HEADING_FONT_STACK});
  `,
  bodyFont: css`
    font-family: var(${CSS_VAR_NAMES.fontFamily}, ${BODY_FONT_STACK});
  `,
};

export const radius = {
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  full: 9999,
};

export const shadows = {
  main: css`
    box-shadow: 0px 8px 32px rgba(0, 0, 0, 0.08), 0px 1px 2px rgba(0, 0, 0, 0.05);
  `,
  searchBox: css`
    box-shadow:
      0px 0px 12px 0px color-mix(in srgb, ${VAR_ACCENT} 30%, transparent),
      0px 4px 20px 0px rgba(17, 17, 17, 0.12);
  `,
  button: css`
    box-shadow:
      0px 0px 12px 0px color-mix(in srgb, ${VAR_ACCENT} 30%, transparent),
      0px 4px 20px 0px rgba(17, 17, 17, 0.12);
  `,
};

export const gradients = {
  buttonBackground: css`
    background:
      linear-gradient(0deg, color-mix(in srgb, ${VAR_INPUT_BG} 90%, white), color-mix(in srgb, ${VAR_INPUT_BG} 90%, white)),
      color-mix(in srgb, ${VAR_ACCENT} 12%, white);
  `,
};

export const borders = {
  input: css`
    border: 1px solid ${VAR_INPUT_BORDER};
  `,
  button: css`
    border: 1px solid color-mix(in srgb, ${VAR_ACCENT} 25%, transparent);
  `,
};

export const spacing = {
  "xs": 4,
  "sm": 8,
  "md": 12,
  "lg": 16,
  "xl": 20,
  "2xl": 24,
  "3xl": 32,
  "4xl": 40,
  "5xl": 48,
  "6xl": 64,
};

export const breakpoints = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
};

// Desktop-first responsive approach
export const responsiveSpacing = {
  safeArea: {
    desktop: spacing["4xl"],
    mobile: spacing["2xl"],
  },
  bottomOffset: {
    desktop: spacing["5xl"],
    mobile: spacing["4xl"],
  },
  landingTopMargin: {
    desktop: 100,
    mobile: spacing["5xl"],
  },
};

export const backgrounds = {
  glassy: css`
    background: ${VAR_INPUT_BG};
    backdrop-filter: blur(6px);
  `,
  overlay: css`
    background: rgba(0, 0, 0, 0.5);
  `,
};

export const iconGradients = {
  sparkle: css`
    background: linear-gradient(138deg,
      color-mix(in srgb, ${VAR_ACCENT} 15%, white) -6.73%,
      ${VAR_ACCENT} 125%);
    border-radius: ${radius.sm}px;
  `,
};

export const resetButton = css`
  border-radius: ${radius.sm}px;
  border: none;
  background: none;
  cursor: pointer;
  padding: 0;
  margin: 0;
  font-size: 16px;
  font-weight: 500;
`;

export const glassyButton = css`
  border-radius: ${radius.lg}px;
  ${borders.button}
  ${gradients.buttonBackground}
  ${shadows.button}
  ${backgrounds.glassy}
  color: ${colors.text};
  font-size: 16px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.md}px;
  padding: ${spacing.lg}px;
  cursor: pointer;
  outline: none;
  transition: all 200ms ease-in-out;

  &:hover {
    background:
      linear-gradient(0deg, color-mix(in srgb, ${VAR_ACCENT} 8%, transparent), color-mix(in srgb, ${VAR_ACCENT} 8%, transparent)),
      linear-gradient(0deg, color-mix(in srgb, ${VAR_INPUT_BG} 90%, white), color-mix(in srgb, ${VAR_INPUT_BG} 90%, white)),
      color-mix(in srgb, ${VAR_ACCENT} 12%, white);
    box-shadow:
      0px 0px 16px 0px color-mix(in srgb, ${VAR_ACCENT} 40%, transparent),
      0px 6px 24px 0px rgba(17, 17, 17, 0.15);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0px);
  }
`;

// Animation speed constants
export const ANIMATION_SPEEDS = {
  FAST: 200,
  STANDARD: 300,
  SLOW: 500,
} as const;

export const transitions = {
  fadeSlide: (duration: number = ANIMATION_SPEEDS.STANDARD) => css`
    transition: opacity ${duration}ms ease-in-out, transform ${duration}ms ease-in-out;
  `,

  fadeOnly: (duration: number = ANIMATION_SPEEDS.STANDARD) => css`
    transition: opacity ${duration}ms ease-in-out;
  `,

  pulse: (duration: number = ANIMATION_SPEEDS.SLOW) => css`
    animation: pulse ${duration}ms ease-in-out infinite alternate;
    
    @keyframes pulse {
      0% {
        opacity: 0.5;
      }
      100% {
        opacity: 1;
      }
    }
  `,
};
